import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { isEduAtom, selectedPlanAtom } from '#atoms';
import { MAX_SEATS_PRO_PLAN } from '#constants';
import { loginInfoQueryCreator } from '#query';
import { useExperimentEnabled } from '#utils';
import { useWorkspace } from './useWorkspace.ts';

export function useSeatCount() {
  const { data: loginInfo, isFetched } = useQuery(loginInfoQueryCreator());
  const { workspace, hasWorkspace, isLoading } = useWorkspace({
    enabled:
      isFetched &&
      loginInfo?.['logged-in'] &&
      !loginInfo?.email_verification_required,
  });
  const selectedPlan = useAtomValue(selectedPlanAtom);
  const isEdu = useAtomValue(isEduAtom);
  const isDefaultOneSeat = useExperimentEnabled('default_one_seat_checkout');

  const minSeatCount = useMemo(() => {
    if (isEdu || !hasWorkspace) {
      return 1;
    }

    const defaultSeats = selectedPlan?.min_seats ?? 0;

    const workspaceMinSeats = Math.max(
      workspace?.min_seats ?? 0,
      workspace?.seats ?? 0,
    );

    return Math.max(defaultSeats, workspaceMinSeats);
  }, [isEdu, workspace, hasWorkspace, selectedPlan]);

  // Doesn't depend on workspace loading — safe to use for initial seat count
  const shouldDefaultToOneSeat =
    isDefaultOneSeat && selectedPlan?.type === 'premium' && !isEdu;
  // Requires workspace members to be loaded — use for UI (checkbox visibility)
  const hasMultipleMembers = (workspace?.members?.length ?? 0) > 1;
  const defaultToOneSeat = shouldDefaultToOneSeat && hasMultipleMembers;

  const effectiveMinSeatCount = shouldDefaultToOneSeat ? 1 : minSeatCount;
  const maxProSeats = defaultToOneSeat
    ? Math.max(MAX_SEATS_PRO_PLAN, minSeatCount)
    : MAX_SEATS_PRO_PLAN;

  function validatedSeatCount({ value }: { value: string }) {
    const val = Number(value);
    const invalidInput = val <= 0;
    const invalidEdu = isEdu && val > 1;
    const invalidPro = selectedPlan?.type === 'premium' && val > maxProSeats;

    const lessThanMinSeatCount = val < effectiveMinSeatCount;

    return !(invalidInput || invalidEdu || invalidPro || lessThanMinSeatCount);
  }

  return {
    validatedSeatCount,
    minSeatCount,
    maxProSeats,
    shouldDefaultToOneSeat,
    defaultToOneSeat,
    isWorkspaceLoading: isLoading,
    maxSeatCount: 10000,
  };
}
